import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import {
  Button, Card, CardBody, Select, Option, Textarea, Typography
} from '@material-tailwind/react'

import { CompanySelector } from '@/shared/selectors/company-selector'
import { EmployeeSelector } from '@/shared/selectors/employee-selector'
import { ContractSelector } from '@/shared/selectors/contract-selector'
import { OfficeSelector } from '@/shared/selectors/office-selector'
import { AssetSelector } from '@/shared/selectors/asset-selector'

const schema = Yup.object().shape({
  office_id: Yup.number().nullable(),
  subject_type: Yup.string().oneOf(['Employee', 'Asset', 'Contractor::Company', 'Contractor::Contract']).required('Requerido'),
  subject_id: Yup.number().typeError('Debe ser numérico').required('Requerido'),
  starts_at: Yup.string().required('Requerido'),
  ends_at: Yup.string().required('Requerido'),
  justification: Yup.string().min(3, 'Muy corto').required('Requerido')
})

export default function AccessControlExceptionsNewPage() {
  const navigate = useNavigate()

  const initialValues = useMemo(() => ({
    office_id: '',
    subject_type: 'Employee',
    subject_id: '',
    starts_at: '',
    ends_at: '',
    justification: ''
  }), [])

  const onSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const payload = {
        access_control_exception: {
          subject_type: values.subject_type,
          subject_id: Number(values.subject_id),
          office_id: values.office_id ? Number(values.office_id) : null,
          event_scope: 'any',
          kind: 'time_window',
          starts_at: values.starts_at,
          ends_at: values.ends_at,
          justification: values.justification
        }
      }
      await axios.post('/api/v1/access_control_exceptions', payload)
      navigate('/controldeacceso/exceptions')
    } catch (err) {
      setStatus(err?.response?.data?.errors?.join(', ') || 'Error al guardar')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <Typography variant="h4">Agregar excepción</Typography>
        <Link to="/controldeacceso/exceptions">
          <Button variant="outlined">Cancelar</Button>
        </Link>
      </div>

      <Card>
        <CardBody>
          <Formik initialValues={initialValues} validationSchema={schema} onSubmit={onSubmit}>
            {({ values, errors, touched, isSubmitting, setFieldValue, status }) => (
              <Form className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <OfficeSelector
                      value={values.office_id ? Number(values.office_id) : undefined}
                      onChange={(opt) => setFieldValue('office_id', opt?.value || '')}
                      required={false}
                      placeholder='Todas las dependencias'
                      isClearable
                    />
                    {touched.office_id && errors.office_id && (
                      <div className="text-red-500 text-xs mt-1">{errors.office_id}</div>
                    )}
                  </div>

                  <div>
                    <Select
                      label="Tipo de sujeto"
                      value={values.subject_type}
                      onChange={(v) => {
                        setFieldValue('subject_type', v)
                        setFieldValue('subject_id', '')
                      }}
                    >
                      <Option value="Employee">Persona</Option>
                      <Option value="Asset">Activo</Option>
                      <Option value="Contractor::Company">Contratista</Option>
                      <Option value="Contractor::Contract">Contrato</Option>
                    </Select>
                    {touched.subject_type && errors.subject_type && (
                      <div className="text-red-500 text-xs mt-1">{errors.subject_type}</div>
                    )}
                  </div>
                </div>

                <div>
                  {values.subject_type === 'Employee' && (
                    <EmployeeSelector
                      value={values.subject_id ? Number(values.subject_id) : undefined}
                      onChange={(opt) => setFieldValue('subject_id', opt?.value || '')}
                      required
                    />
                  )}
                  {values.subject_type === 'Contractor::Company' && (
                    <CompanySelector
                      value={values.subject_id ? Number(values.subject_id) : undefined}
                      onChange={(opt) => setFieldValue('subject_id', opt?.value || '')}
                      required
                    />
                  )}
                  {values.subject_type === 'Contractor::Contract' && (
                    <ContractSelector
                      value={values.subject_id ? Number(values.subject_id) : undefined}
                      onChange={(opt) => setFieldValue('subject_id', opt?.value || '')}
                      required
                    />
                  )}
                  {values.subject_type === 'Asset' && (
                    <AssetSelector
                      value={values.subject_id ? Number(values.subject_id) : undefined}
                      onChange={(opt) => setFieldValue('subject_id', opt?.value || '')}
                      valueField='id'
                      required
                    />
                  )}
                  {touched.subject_id && errors.subject_id && (
                    <div className="text-red-500 text-xs mt-1">{errors.subject_id}</div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Typography variant="small" className="mb-1 block">Comienza</Typography>
                    <input
                      type="datetime-local"
                      value={values.starts_at}
                      onChange={(e) => setFieldValue('starts_at', e.target.value)}
                      className="block w-full min-w-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 text-[16px] focus:border-blue-500 focus:outline-none"
                    />
                    {touched.starts_at && errors.starts_at && (
                      <div className="text-red-500 text-xs mt-1">{errors.starts_at}</div>
                    )}
                  </div>
                  <div>
                    <Typography variant="small" className="mb-1 block">Termina</Typography>
                    <input
                      type="datetime-local"
                      value={values.ends_at}
                      onChange={(e) => setFieldValue('ends_at', e.target.value)}
                      className="block w-full min-w-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 text-[16px] focus:border-blue-500 focus:outline-none"
                    />
                    {touched.ends_at && errors.ends_at && (
                      <div className="text-red-500 text-xs mt-1">{errors.ends_at}</div>
                    )}
                  </div>
                </div>

                <div>
                  <Textarea
                    label="Justificación"
                    value={values.justification}
                    onChange={(e) => setFieldValue('justification', e.target.value)}
                  />
                  {touched.justification && errors.justification && (
                    <div className="text-red-500 text-xs mt-1">{errors.justification}</div>
                  )}
                </div>

                {status && <div className="text-red-600 text-sm">{status}</div>}

                <div className="flex gap-2 justify-end">
                  <Link to="/controldeacceso/exceptions">
                    <Button variant="outlined">Cancelar</Button>
                  </Link>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Guardando…' : 'Guardar excepción'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>
    </div>
  )
}
